import React, { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { X, Send, MapPin, Sparkles } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";

const CONFETTI_COLORS = ["#c39d2b", "#e5c65b", "#722f37", "#8c3a45", "#f4ead0", "#a08a70"];

/**
 * Full-screen celebratory reveal that plays when a match is unlocked.
 *
 * Props:
 *   match: { match_id, user: { display_name, age, location, bio, photos, verified } }
 *   onClose: () => void
 */
export default function MatchRevealModal({ match, onClose }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    // Prevent background scroll while the celebration is on-screen
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Stable confetti configuration so React doesn't re-shuffle on every render
  const confetti = useMemo(() => {
    return Array.from({ length: 42 }).map(() => {
      const angle = Math.random() * Math.PI * 2;
      const distance = 180 + Math.random() * 220;
      return {
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        tx: Math.cos(angle) * distance,
        ty: Math.sin(angle) * distance - 40,
        rotate: 180 + Math.random() * 540,
        delay: Math.random() * 220,
        width: 5 + Math.random() * 6,
        height: 8 + Math.random() * 10,
      };
    });
  }, [match?.match_id]);

  if (!match) return null;
  const u = match.user;
  const photo = u.photos?.[0];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center animate-reveal-backdrop"
      style={{ background: "rgba(20, 12, 12, 0.72)" }}
      data-testid="match-reveal-modal"
      onClick={onClose}
    >
      {/* Confetti burst — rendered above the backdrop, behind the card */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute left-1/2 top-1/2 w-0 h-0">
          {confetti.map((c, i) => (
            <span
              key={i}
              className="reveal-confetti-piece"
              style={{
                background: c.color,
                width: c.width,
                height: c.height,
                animationDelay: `${c.delay}ms`,
                "--tx": `${c.tx}px`,
                "--ty": `${c.ty}px`,
                "--r": `${c.rotate}deg`,
              }}
            />
          ))}
        </div>
      </div>

      <div
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Match revealed"
        className="relative w-[92vw] max-w-md rounded-[2rem] overflow-hidden bg-white card-elev animate-reveal-pop"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow"
          aria-label="Close"
          data-testid="match-reveal-close"
        >
          <X className="w-4 h-4 text-[#722f37]" />
        </button>

        <div className="relative">
          {photo?.data_url ? (
            <img
              src={photo.data_url}
              alt={u.display_name}
              className="w-full h-[420px] object-cover animate-reveal-unblur"
              data-testid="match-reveal-photo"
            />
          ) : (
            <div className="w-full h-[420px] bg-[#eae5d9]" />
          )}

          {/* Gold shimmer sweep across the photo */}
          <div className="absolute inset-0 reveal-shimmer" />

          {/* Sparkle accent */}
          <div
            className="absolute top-4 left-4 animate-reveal-fade-up"
            style={{ animationDelay: "700ms" }}
          >
            <div className="glass rounded-full px-3 py-1.5 flex items-center gap-1.5 text-xs font-semibold text-[#722f37]">
              <Sparkles className="w-3.5 h-3.5 text-[#c39d2b]" />
              It's a match
            </div>
          </div>

          {/* Name overlay */}
          <div
            className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/85 via-black/40 to-transparent animate-reveal-fade-up"
            style={{ animationDelay: "900ms" }}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-serif-display text-white text-5xl leading-none" data-testid="match-reveal-name">
                {u.display_name}, <span className="opacity-80">{u.age}</span>
              </h2>
              {u.verified && <VerifiedBadge size={14} />}
            </div>
            {u.location && (
              <div className="mt-2 flex items-center gap-1 text-white/85 text-sm">
                <MapPin className="w-3.5 h-3.5" /> {u.location}
              </div>
            )}
          </div>
        </div>

        <div
          className="px-6 py-6 animate-reveal-fade-up"
          style={{ animationDelay: "1200ms" }}
        >
          {u.bio && (
            <p className="text-[#1a1a1a] leading-relaxed text-base line-clamp-4">
              {u.bio}
            </p>
          )}
          <div className="mt-6 grid grid-cols-2 gap-3">
            <button
              onClick={onClose}
              className="btn-outline flex items-center justify-center"
              data-testid="match-reveal-later"
            >
              Later
            </button>
            <Link
              to={`/chat/${match.match_id}`}
              className="btn-primary flex items-center justify-center gap-2"
              data-testid="match-reveal-say-hi"
            >
              <Send className="w-4 h-4" /> Say hello
            </Link>
          </div>
          <Link
            to="/pricing?promo=SAVE20"
            className="mt-4 block text-center text-xs font-semibold text-[#8a6a10] bg-[#c39d2b]/15 border border-[#c39d2b]/40 rounded-full py-2 hover:bg-[#c39d2b]/25 transition-colors"
            data-testid="match-reveal-promo"
          >
            🎁 Enjoyed this? Save 20% on a 3-pack of reveals — code SAVE20
          </Link>
        </div>
      </div>
    </div>
  );
}
